'use client';
import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { LANGS, type Lang } from '@lms/i18n';
import { useT } from '../../lib/i18n';
import { ThemeToggle } from '../../components/ThemeToggle';
import { toast } from '../../components/Toast';
import { setDebugEnabled, setNextUpEnabled, useDebugEnabled, useNextUpEnabled } from '../../components/player/next-up-pref';

/** 서버(`resolveLang`)가 읽는 쿠키와 같은 이름이어야 한다 */
const LANG_COOKIE = 'sp_lang';
const YEAR = 60 * 60 * 24 * 365;

/**
 * 설정 — 말과 화면 모드. **누르는 즉시 반영한다**(저장 버튼이 없다).
 *
 * 말은 **쿠키에 담는다** — 서버 렌더가 첫 화면부터 그 말로 그려야 하기 때문이다
 * (`site.ts` 의 `resolveLang`). 담은 뒤 `router.refresh()` 로 셸까지 다시 받는다.
 * localStorage 로 하면 서버는 모른 채 그리고 브라우저가 뒤늦게 바꿔 화면이 번쩍인다.
 *
 * **[자동]을 남겨 둔다** — 기능정의서 §16-3 의 기본은 자동 감지(OS·브라우저 언어)다.
 * 한 번 고르면 되돌릴 길이 없으면 그 기본으로 돌아갈 수 없다. 고르면 쿠키를 지운다.
 *
 * 화면 모드는 마이페이지 좌측 메뉴에 있던 **그 토글**이다(`ThemeToggle`). 2026-09-05 사용자
 * 지시로 그쪽에서는 걷었다 — 같은 값을 두 곳에서 켜면 어느 쪽이 맞는지 헷갈리고, 휴대폰에서는
 * 그 메뉴가 통째로 숨어 반쪽만 쓸 수 있었다. **이제 여기 하나뿐이다.**
 */
export function SettingsForm({ current, picked: initial, isAdmin = false }: {
  /** 지금 이 화면이 그려진 말 — [자동]일 때 "그래서 뭘로 나오나" 를 적는다 */
  current: Lang;
  /** 고정해 둔 말(쿠키). 비어 있으면 자동 감지다 — **서버가 읽어 넘긴다** */
  picked: Lang | '';
  /**
   * 관리자인가 — **재생 진단은 관리자에게만 보인다**(2026-09-17 사용자 지시).
   * 점검용 장치라 일반 회원에게는 있을 까닭이 없다. 판정은 서버가 한다(`me.role`).
   */
  isAdmin?: boolean;
}) {
  const t = useT();
  const router = useRouter();
  const [picked, setPicked] = useState<Lang | ''>(initial);
  /*
   * 다음 강의 안내(2026-09-16 사용자 지시) — 재생 창 오른쪽 아래에 뜨는 그 카드다. 값은 이 브라우저에 남는다
   * (화면 모드와 같은 결). 서버 렌더에는 저장소가 없어 기본(켬)으로 그려 두고, 브라우저에서 실제 값으로 맞춘다.
   */
  const nextUp = useNextUpEnabled();
  // 재생 진단(2026-09-17) — 점검용이라 기본은 꺼짐이다
  const debug = useDebugEnabled();

  const change = (value: Lang | '') => {
    setPicked(value);
    document.cookie = value
      ? `${LANG_COOKIE}=${value}; path=/; max-age=${YEAR}; samesite=lax`
      : `${LANG_COOKIE}=; path=/; max-age=0; samesite=lax`;
    toast(t('set.saved'));
    router.refresh();
  };

  return (
    <div className="mi-section mb16">
      <div className="mi-section-header">
        <span className="lec-sec-bar"></span>
        <h2 className="mi-section-title">{t('set.display')}</h2>
      </div>
      <div className="mi-form-body">
        <div className="form-wrap">
          <div className="form-field">
            <label className="form-label" htmlFor="set-lang">{t('set.language')}</label>
            <select id="set-lang" className="form-input" value={picked}
              onChange={(e) => change(e.target.value as Lang | '')}>
              <option value="">{t('set.langAuto')}</option>
              {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
            {/* 지금 무슨 말로 보고 있는지 — [자동]일 때 "그래서 뭘로 나온다는 건가" 에 답한다 */}
            <p className="form-hint">
              {picked ? t('set.langHint') : t('set.langAutoHint', { lang: labelOf(current) })}
            </p>
          </div>

          <div className="form-field">
            <label className="form-label">{t('set.theme')}</label>
            <ThemeToggle />
            <p className="form-hint">{t('set.themeHint')}</p>
          </div>

          <div className="form-field">
            <label className="form-label">{t('set.nextUp')}</label>
            {/* 화면 모드와 같은 토글(`tgl-wrap`) — 상태는 `aria-pressed` 가 말한다(web.css).
                토스트는 띄우지 않는다(2026-09-16 사용자 지시) — 옆 글자가 곧 바뀌어 눌린 것이 보이고, 화면 모드도 조용하다 */}
            <div className="tgl-wrap">
              <button type="button" className="tgl" aria-pressed={nextUp} aria-label={t('set.nextUp')}
                onClick={() => setNextUpEnabled(!nextUp)} />
              <span className="fs12">{nextUp ? t('set.nextUpUse') : t('set.nextUpOff')}</span>
            </div>
            <p className="form-hint">{t('set.nextUpHint')}</p>
          </div>

          {/* 재생 진단 — 점검용이라 **관리자에게만** 보인다(2026-09-17 사용자 지시). 기본은 꺼짐 */}
          {isAdmin && (
            <div className="form-field">
              <label className="form-label">{t('set.playerDebug')}</label>
              <div className="tgl-wrap">
                <button type="button" className="tgl" aria-pressed={debug} aria-label={t('set.playerDebug')}
                  onClick={() => setDebugEnabled(!debug)} />
                <span className="fs12">{debug ? t('set.nextUpUse') : t('set.nextUpOff')}</span>
              </div>
              <p className="form-hint">{t('set.playerDebugHint')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const labelOf = (code: Lang) => LANGS.find((l) => l.code === code)?.label ?? code;
